import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { ClashResult } from '@viewer/clash/lib'
import { formatMm } from './clashBcf'
import { viewerApi } from '@viewer/lib/viewerApi'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import type { NomeDoModelo } from '@viewer/lib/nomeDoModelo'
import { namesFor } from './clashModels'
import { groupResults, type ClashGroupBy } from './clashSets'
import {
  clashKey,
  PRIORITIES,
  PRIORITY_LABELS,
  triageOf,
  TRIAGE_LABELS,
  type ClashPriority,
  type ClashTriageMap,
  type ClashTriagePatch,
  type ClashTriageState,
} from './clashTriage'
import ClashTriageEditor from './ClashTriageEditor'

// ---------------------------------------------------------------------------
// Lista de conflitos: agrupada, ordenada por gravidade dentro do grupo,
// com a triagem a mao.
//
// Cada linha diz o que e preciso para decidir sem abrir mais nada: as
// duas categorias, os nomes dos elementos, o tipo (duro / folga) e a
// medida. A profundidade aparece sempre com o "aprox.": e uma sondagem,
// nao uma medicao (docs/CLASH.md).
// ---------------------------------------------------------------------------

const STATES: ClashTriageState[] = ['novo', 'aceite', 'resolvido']

const PRIORITY_STYLE: Record<ClashPriority, string> = {
  critica: 'text-[var(--color-error-text)]',
  alta: 'text-[var(--color-warn-text)]',
  media: 'text-[var(--color-text-muted)]',
  baixa: 'text-[var(--color-text-dim)]',
}

const STATE_STYLE: Record<ClashTriageState, string> = {
  novo: 'text-[var(--color-text-muted)]',
  aceite: 'text-[var(--color-warn-text)]',
  resolvido: 'text-[var(--color-ok-text)]',
  reaberto: 'text-[var(--color-error-text)]',
}

/** Nomes dos elementos, em lote por modelo. Uma ida ao Fragments por
 *  modelo e nao uma por linha; o resultado fica em cache de modulo
 *  porque os nomes nao mudam enquanto o modelo estiver carregado.
 *
 *  A chave e a INSTANCIA do modelo carregado, nao o modelId: o modelId e
 *  o nome do ficheiro, e reabrir uma versao nova de Projeto.ifc mostrava
 *  os nomes da versao anterior para os mesmos localIds. */
const nameCache = new WeakMap<object, Map<number, string | null>>()

function namesOf(modelId: string): Map<number, string | null> | null {
  const model = viewerApi.getModel(modelId) as object | null | undefined
  if (!model) return null
  let names = nameCache.get(model)
  if (!names) {
    names = new Map()
    nameCache.set(model, names)
  }
  return names
}

function useClashNames(results: readonly ClashResult[]): number {
  const [version, setVersion] = useState(0)
  useEffect(() => {
    let cancelled = false
    const missing = new Map<string, Set<number>>()
    const want = (modelId: string, localId: number): void => {
      if (namesOf(modelId)?.has(localId)) return
      let set = missing.get(modelId)
      if (!set) {
        set = new Set<number>()
        missing.set(modelId, set)
      }
      set.add(localId)
    }
    for (const r of results) {
      want(r.pair.modelA, r.pair.localIdA)
      want(r.pair.modelB, r.pair.localIdB)
    }
    if (missing.size === 0) return
    void (async () => {
      for (const [modelId, ids] of missing) {
        const list = [...ids]
        const names = await namesFor(modelId, list)
        const cache = namesOf(modelId)
        if (cache) list.forEach((id, i) => cache.set(id, names[i]))
      }
      if (!cancelled) setVersion((v) => v + 1)
    })()
    return () => {
      cancelled = true
    }
  }, [results])
  return version
}

function elementLabel(
  modelId: string,
  localId: number,
  category: string | null | undefined,
  modelo?: NomeDoModelo | null,
): string {
  const name = namesOf(modelId)?.get(localId)
  const cat = category ?? 'sem categoria'
  // Com varios modelos, a sigla (ou o nome curto) do modelo a frente:
  // o mesmo nome curto do resto da plataforma (nomeDoModelo).
  const prefixo = modelo ? `${modelo.minimo} · ` : ''
  return name ? `${prefixo}${cat} · ${name} · #${localId}` : `${prefixo}${cat} · #${localId}`
}

export interface ClashResultsListProps {
  results: ClashResult[]
  groupBy: ClashGroupBy
  triage: ClashTriageMap
  checked: Set<string>
  focusKey: string | null
  onOpen: (r: ClashResult) => void
  onTriage: (r: ClashResult, state: ClashTriageState) => void
  /** Campos da triagem (prioridade, responsável, nota, ...). */
  onPatch: (r: ClashResult, patch: ClashTriagePatch) => void
  onToggleChecked: (key: string) => void
  /** Marca ou desmarca todos os conflitos de um grupo da lista. */
  onSetChecked?: (keys: string[], on: boolean) => void
  /** Nomes dos modelos abertos, para as sugestões de especialidade. */
  modelos?: readonly string[]
}

export default function ClashResultsList({
  results,
  groupBy,
  triage,
  checked,
  focusKey,
  onOpen,
  onTriage,
  onPatch,
  onToggleChecked,
  onSetChecked,
  modelos = [],
}: ClashResultsListProps) {
  useClashNames(results)
  const { nomeDe, varios } = useNomesDosModelos()
  const modeloDe = (id: string) => (varios ? nomeDe(id) : null)
  const groups = useMemo(() => {
    // Grupos da triagem: a prioridade do grupo e a mais alta dos seus
    // conflitos; a lista sai por prioridade.
    const ordemDe = new Map<string, number>()
    if (groupBy === 'grupos') {
      for (const r of results) {
        const e = triage[clashKey(r)]
        if (!e?.group) continue
        const o = e.priority ? PRIORITIES.indexOf(e.priority) : 4
        ordemDe.set(e.group, Math.min(ordemDe.get(e.group) ?? 4, o))
      }
    }
    return groupResults(results, groupBy, (id) => nomeDe(id)?.curto ?? id, (r) => {
      const e = triage[clashKey(r)]
      if (!e?.group) return null
      const ordem = ordemDe.get(e.group) ?? 4
      const prio = ordem < 4 ? `${PRIORITY_LABELS[PRIORITIES[ordem]]} · ` : ''
      return { id: e.group, titulo: `${prio}${e.groupTitle ?? e.group}`, ordem }
    })
  }, [results, groupBy, nomeDe, triage])
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [noteOpen, setNoteOpen] = useState<string | null>(null)

  if (results.length === 0) {
    return (
      <p className="px-3 py-3 text-[11px] italic text-[var(--color-text-dim)]">
        Nenhum conflito nesta configuração.
      </p>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1">
      {groups.map((g) => {
        const isOpen = !collapsed.has(g.key)
        const chaves = g.results.map(clashKey)
        const marcados = chaves.filter((k) => checked.has(k)).length
        return (
          <div key={g.key} className="mb-1">
            <div className="flex items-center gap-1">
            {onSetChecked && (
              <input
                type="checkbox"
                checked={marcados === chaves.length && chaves.length > 0}
                ref={(el) => {
                  if (el) el.indeterminate = marcados > 0 && marcados < chaves.length
                }}
                onChange={() => onSetChecked(chaves, marcados < chaves.length)}
                title="Marcar o grupo inteiro (para a edição em lote ou para exportar)"
                aria-label={`Marcar o grupo ${g.label}`}
                className="accent-[var(--color-accent)]"
              />
            )}
            <button
              type="button"
              onClick={() =>
                setCollapsed((prev) => {
                  const next = new Set(prev)
                  if (next.has(g.key)) next.delete(g.key)
                  else next.add(g.key)
                  return next
                })
              }
              className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[11px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]"
            >
              {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="truncate">{g.label}</span>
              <span className="ml-auto font-mono text-[10px]">
                {g.results.length}
              </span>
            </button>
            </div>
            {isOpen &&
              g.results.map((r) => {
                const key = clashKey(r)
                const entry = triageOf(triage, key)
                const isFocus = focusKey === key
                return (
                  <div
                    key={key}
                    className={`mb-0.5 rounded border ${
                      isFocus
                        ? 'border-[var(--color-accent)]'
                        : 'border-[var(--color-border)]/40'
                    } bg-[var(--color-bg)]/40`}
                  >
                    <div className="flex items-start gap-1 px-1 py-1">
                      <input
                        type="checkbox"
                        checked={checked.has(key)}
                        onChange={() => onToggleChecked(key)}
                        title="Marcar para exportar"
                        aria-label="Marcar conflito para exportar"
                        className="mt-0.5 accent-[var(--color-accent)]"
                      />
                      <button
                        type="button"
                        onClick={() => onOpen(r)}
                        className="min-w-0 flex-1 text-left"
                        title={`Ir ao conflito: enquadra a zona e seleciona os dois elementos (${nomeDe(r.pair.modelA)?.completo ?? r.pair.modelA} x ${nomeDe(r.pair.modelB)?.completo ?? r.pair.modelB})`}
                      >
                        <span className="block truncate text-[11px] text-[var(--color-text)]">
                          {elementLabel(
                            r.pair.modelA,
                            r.pair.localIdA,
                            r.categoryA,
                            modeloDe(r.pair.modelA),
                          )}
                        </span>
                        <span className="block truncate text-[11px] text-[var(--color-text-muted)]">
                          {elementLabel(
                            r.pair.modelB,
                            r.pair.localIdB,
                            r.categoryB,
                            modeloDe(r.pair.modelB),
                          )}
                        </span>
                        <span className="block text-[10px] text-[var(--color-text-dim)]">
                          {r.kind === 'hard'
                            ? `duro · ${formatMm(r.depth)} aprox.`
                            : `folga · ${formatMm(r.distance)}`}
                          {r.evidence === 'containment' ? ' · contenção' : ''}
                          {r.truncated ? ' · truncado' : ''}
                        </span>
                        {(entry.priority || entry.assignedTo || entry.dueDate) && (
                          <span className="block text-[10px] text-[var(--color-text-dim)]">
                            {entry.priority && (
                              <span className={PRIORITY_STYLE[entry.priority]}>
                                {PRIORITY_LABELS[entry.priority]}
                              </span>
                            )}
                            {entry.assignedTo ? ` · ${entry.assignedTo}` : ''}
                            {entry.dueDate ? ` · até ${entry.dueDate.split('-').reverse().join('/')}` : ''}
                          </span>
                        )}
                      </button>
                      <div className="flex shrink-0 flex-col items-end gap-0.5">
                        <div className="flex gap-0.5">
                          {STATES.map((s) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => onTriage(r, s)}
                              title={TRIAGE_LABELS[s]}
                              className={`rounded border px-1 py-px text-[9px] ${
                                entry.state === s
                                  ? `border-[var(--color-accent)] ${STATE_STYLE[s]}`
                                  : 'border-[var(--color-border)]/50 text-[var(--color-text-dim)]'
                              }`}
                            >
                              {TRIAGE_LABELS[s].slice(0, 1)}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setNoteOpen((cur) => (cur === key ? null : key))
                          }
                          title="Prioridade, responsável, recomendação, prazo, etiquetas e nota"
                          aria-expanded={noteOpen === key}
                          className={`text-[9px] ${entry.note || entry.recommendation ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-dim)]'}`}
                        >
                          {entry.note || entry.recommendation ? 'triagem ✓' : 'triagem'}
                        </button>
                      </div>
                    </div>
                    {noteOpen === key && (
                      <div className="border-t border-[var(--color-border)]/40 px-1 py-1">
                        <ClashTriageEditor
                          entry={entry}
                          modelos={modelos}
                          onPatch={(patch) => onPatch(r, patch)}
                        />
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
        )
      })}
    </div>
  )
}
